import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, RefreshCw, Database, FileText, Truck, Receipt, Calendar, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminTools = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [fixingNumbers, setFixingNumbers] = useState(false);
  const [nextNumDocType, setNextNumDocType] = useState('delivery_order');
  const [nextNumValue, setNextNumValue] = useState('');
  const [settingNextNum, setSettingNextNum] = useState(false);

  const handleSetNextNumber = async () => {
    if (!nextNumValue) { toast.error('Enter the next number first'); return; }
    setSettingNextNum(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/set-next-number`, {
        document_type: nextNumDocType,
        next_number: parseInt(nextNumValue)
      });
      toast.success(res.data.message, { duration: 8000 });
      setNextNumValue('');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to set next number'));
    } finally {
      setSettingNextNum(false);
    }
  };

  const handleFixNumbering = async () => {
    setFixingNumbers(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/fix-numbering`);
      const invRep = res.data.report?.invoice;
      const fixed = invRep?.renumbered?.length || 0;
      toast.success(`${res.data.message}${fixed ? ` (${fixed} duplicate invoice number(s) renumbered)` : ''}`, { duration: 8000 });
      fetchStats();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to repair numbering'));
    } finally {
      setFixingNumbers(false);
    }
  };

  const handleTestWhatsApp = async () => {
    setTestingWhatsApp(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/test-whatsapp`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message, { duration: 10000 });
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'WhatsApp test failed'));
    } finally {
      setTestingWhatsApp(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/database-stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load database stats');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'DELETE ALL') {
      toast.error('Please type "DELETE ALL" to confirm');
      return;
    }

    setResetting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/reset-database`, {
        confirm: 'CONFIRM_RESET'
      });

      if (response.data.success) {
        toast.success('Database cleared successfully! All sequences reset to 001.');
        setShowConfirmDialog(false);
        setConfirmText('');
        fetchStats();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset database'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Admin Tools</h1>
          <p className="text-gray-600 mt-1">Database management and system utilities</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Database Stats */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Database size={24} className="text-blue-600" />
                  Database Statistics
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : stats ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={18} className="text-blue-600" />
                        <span className="text-sm text-gray-600">Quotations</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">{stats.document_counts.quotations_billing}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Receipt size={18} className="text-green-600" />
                        <span className="text-sm text-gray-600">Invoices</span>
                      </div>
                      <p className="text-2xl font-bold text-green-600">{stats.document_counts.invoices}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck size={18} className="text-orange-600" />
                        <span className="text-sm text-gray-600">Delivery Orders</span>
                      </div>
                      <p className="text-2xl font-bold text-orange-600">{stats.document_counts.delivery_orders}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Receipt size={18} className="text-purple-600" />
                        <span className="text-sm text-gray-600">Payment Receipts</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-600">{stats.document_counts.payment_receipts}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-gray-600" />
                        <span className="text-sm text-gray-600">Bookings</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-600">{stats.document_counts.bookings}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={18} className="text-gray-600" />
                        <span className="text-sm text-gray-600">Contact Requests</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-600">{stats.document_counts.contact_requests}</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Current Sequence Numbers</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Next Invoice:</span>
                        <span className="ml-2 font-mono font-bold">INV-2026-{String((stats.current_sequences.invoice || 0) + 1).padStart(3, '0')}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Next DO:</span>
                        <span className="ml-2 font-mono font-bold">DO-{String((stats.current_sequences.delivery_order || 0) + 1).padStart(3, '0')}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Next Quotation:</span>
                        <span className="ml-2 font-mono font-bold">QT-2026-{String((stats.current_sequences.quotation || 0) + 1).padStart(3, '0')}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Next Receipt:</span>
                        <span className="ml-2 font-mono font-bold">PR-2026-{String((stats.current_sequences.payment_receipt || 0) + 1).padStart(3, '0')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </CardContent>
          </Card>

          {/* Reset Counters Section */}
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                ↻ Reset Document Numbers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">Reset Maintenance Checklist Counter</h4>
                <p className="text-sm text-orange-700 mb-3">
                  Reset the checklist sequence so the next submission starts at <strong>CHK-{new Date().getFullYear()}-001</strong>.
                  Existing checklists keep their original numbers.
                </p>
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm('Reset checklist counter? Next submission will be CHK-' + new Date().getFullYear() + '-001.')) return;
                    try {
                      const res = await axios.post(`${BACKEND_URL}/api/maintenance-checklist/reset-counter`);
                      toast.success(`✓ Reset. Next checklist will be ${res.data.next_number}`);
                    } catch (e) {
                      toast.error(getErrorMessage(e, 'Reset failed'));
                    }
                  }}
                  className="border-orange-400 text-orange-700 hover:bg-orange-100"
                  data-testid="reset-checklist-counter-btn"
                >
                  ↻ Reset Checklist No.
                </Button>
              </div>

              <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">Reset Other Document Counters</h4>
                <p className="text-sm text-orange-700 mb-3">
                  Pick a document type — its next created document will start at <strong>-001</strong> for {new Date().getFullYear()}.
                  Existing documents keep their numbers.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { type: 'quotation', label: 'Quotation (QT)', prefix: 'QT' },
                    { type: 'delivery_order', label: 'Delivery Order (DO)', prefix: 'DO' },
                    { type: 'invoice', label: 'Invoice (INV)', prefix: 'INV' },
                    { type: 'payment_voucher', label: 'Customer Receipt (OR)', prefix: 'OR' },
                    { type: 'supplier_payment', label: 'Supplier Payment (PV)', prefix: 'PV' },
                    { type: 'statement_of_account', label: 'SOA', prefix: 'SOA' },
                  ].map(({ type, label, prefix }) => (
                    <Button
                      key={type}
                      variant="outline"
                      size="sm"
                      className="border-orange-400 text-orange-700 hover:bg-orange-100 justify-start"
                      data-testid={`reset-counter-${type}`}
                      onClick={async () => {
                        if (!window.confirm(`Reset ${label} counter? Next document will be ${prefix}-${new Date().getFullYear()}-001.`)) return;
                        try {
                          const res = await axios.post(`${BACKEND_URL}/api/admin/reset-document-counter`, { document_type: type });
                          toast.success(`✓ Reset. Next ${label} will be ${res.data.next_number}`);
                        } catch (e) {
                          toast.error(getErrorMessage(e, 'Reset failed'));
                        }
                      }}
                    >
                      ↻ {label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Numbering Repair */}
          <Card className="border-amber-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <RefreshCw size={24} />
                Repair Document Numbering
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-3">
                  If your invoice/quotation numbers went backwards (e.g. back to 001), click this to renumber any duplicates and continue counting from the highest existing number.
                </p>
                <Button
                  onClick={handleFixNumbering}
                  disabled={fixingNumbers}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="fix-numbering-btn"
                >
                  <RefreshCw size={18} className="mr-2" />
                  {fixingNumbers ? 'Repairing...' : 'Repair Numbering Now'}
                </Button>
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Set next number manually</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={nextNumDocType}
                      onChange={(e) => setNextNumDocType(e.target.value)}
                      className="h-9 text-sm border rounded px-2 bg-white"
                      data-testid="next-number-doctype-select"
                    >
                      <option value="delivery_order">Delivery Order (DO)</option>
                      <option value="invoice">Invoice (INV)</option>
                      <option value="quotation">Quotation (QT)</option>
                      <option value="payment_receipt">Payment Receipt (PR)</option>
                      <option value="payment_voucher">Official Receipt (OR)</option>
                      <option value="supplier_payment">Payment Voucher (PV)</option>
                      <option value="statement_of_account">SOA</option>
                    </select>
                    <Input
                      type="number"
                      min="1"
                      placeholder="e.g. 92"
                      value={nextNumValue}
                      onChange={(e) => setNextNumValue(e.target.value)}
                      className="h-9 w-28 bg-white"
                      data-testid="next-number-input"
                    />
                    <Button
                      onClick={handleSetNextNumber}
                      disabled={settingNextNum}
                      variant="outline"
                      className="h-9 border-amber-600 text-amber-700 hover:bg-amber-100"
                      data-testid="set-next-number-btn"
                    >
                      {settingNextNum ? 'Saving...' : 'Set Next Number'}
                    </Button>
                  </div>
                  <p className="text-xs text-amber-700 mt-2">Example: set Delivery Order to 92 → your next DO will be DO-092 (if 092 is already used, it will take the next free number).</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp Connection Test */}
          <Card className="border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <MessageCircle size={24} />
                WhatsApp Connection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 mb-3">
                  Sends a test message to your phone to check the Twilio sandbox session is alive.
                  If it fails, re-send <strong>join ran-tightly</strong> to <strong>+1 415 523 8886</strong> on WhatsApp, then test again.
                </p>
                <Button
                  onClick={handleTestWhatsApp}
                  disabled={testingWhatsApp}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="test-whatsapp-btn"
                >
                  <MessageCircle size={18} className="mr-2" />
                  {testingWhatsApp ? 'Sending test...' : 'Send Test WhatsApp'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle size={24} />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Reset Database</h4>
                <p className="text-sm text-red-700 mb-4">
                  This will permanently delete ALL data including bookings, invoices, delivery orders, 
                  quotations, and payment receipts. Document sequences will be reset to start from 001.
                  <strong> This action cannot be undone.</strong>
                </p>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowConfirmDialog(true)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="reset-database-btn"
                >
                  <Trash2 size={18} className="mr-2" />
                  Reset All Data
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Confirm Database Reset
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Type <strong>&quot;DELETE ALL&quot;</strong> to confirm:</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE ALL"
              className="mt-2"
              data-testid="confirm-reset-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConfirmDialog(false);
              setConfirmText('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReset}
              disabled={confirmText !== 'DELETE ALL' || resetting}
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Resetting...
                </span>
              ) : (
                'Yes, Delete Everything'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
